import React, { useMemo } from 'react'
import { CircularProgress } from '@mui/material'
import 'leaflet/dist/leaflet.css'
import { TileLayer, MapContainer, CircleMarker, Polygon, Popup, Circle } from 'react-leaflet'
import { pivot } from '../../data/mapsData'
import { formatNumberUsingUnit } from '../../utils/formatUtils'
import BouncingLogo from '../../components/Shimmer/BouncingLogo'
import { ZipCodePopupComponent } from './Components/ZipCodePopupComponent'

const MapView = ({
  data,
  isLoading,
  showShowrooms,
  countyAreas,
  wonOppsData,
  view,
  zipcodeAreas,
  showWonOpps
}) => {
  const MAP_CENTER = [40.7128, -74.006]
  // const MAP_CENTER = [39.742043, -104.991531]

  const areasCounty = useMemo(() => countyAreas, [countyAreas])
  const areasZipcode = useMemo(() => zipcodeAreas, [zipcodeAreas])
  let wonOpps = useMemo(() => wonOppsData, [wonOppsData])

  const checkPointerCoordinates = coord => {
    if (coord?.length > 0) {
      if (coord[0] < 0) {
        return [coord[1], coord[0]]
      } else {
        return [coord[0], coord[1]]
      }
    }
    return undefined
  }

  const configureZipcodeCoordinates = area => {
    if (area) {
      if (area?.type === 'Point') {
        return area?.coordinates
      } else {
        return Array.isArray(area?.coordinates[0])
          ? area?.coordinates[0]?.map(item => (item[0] < 0 ? [item[1], item[0]] : item))
          : []
      }
    }
    return undefined
  }

  return (
    <MapContainer
      center={MAP_CENTER}
      zoom={5}
      maxZoom={20}
      attributionControl={true}
      zoomControl={true}
      doubleClickZoom={true}
      scrollWheelZoom={true}
      dragging={true}
      easeLinearity={0.35}
      style={{ height: '97%', width: '85%' }}
    >
      {isLoading ? (
        <>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              width: '100%',
              height: '100%'
            }}
          >
            <BouncingLogo height='2vh' zIndex='100000' text='Loading...' />
          </div>
        </>
      ) : (
        <>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {showShowrooms &&
            data?.map(pointer => (
              <CircleMarker
                center={checkPointerCoordinates(pointer?.centre)}
                fillColor="#ffeb3b"
                radius={3}
                stroke={false}
                fillOpacity={1}
                weight={1}
              >
                <Popup>{pointer?.name}</Popup>
              </CircleMarker>
            ))}

          {showWonOpps &&
            wonOpps &&
            wonOpps?.map(pointer => {
              if (
                pointer?.coordinates &&
                pointer?.coordinates !== 'Not Available' &&
                pointer?.coordinates !== null
              )
                return (
                  <CircleMarker
                    center={checkPointerCoordinates(pointer?.coordinates?.geometry?.coordinates)}
                    fillColor="rgba(129, 199, 132, 1)"
                    radius={3}
                    stroke={false}
                    fillOpacity={1}
                    weight={1}
                  >
                    <Popup>{pointer?.address}</Popup>
                  </CircleMarker>
                )
              else return null
            })}
          {view === 'Zip Code' &&
            areasZipcode &&
            areasZipcode.length > 0 &&
            areasZipcode.map((area, index) => {
              const coordinates = area?.coordinates?.geometry?.coordinates;
              const isPoint = area?.coordinates?.geometry?.type === 'Point';
              const hasValidCoordinates = Array.isArray(coordinates) && coordinates.length > 0;
              return (
                <>
                  {hasValidCoordinates ? (
                    isPoint ? (
                      <Circle center={[coordinates?.[1], coordinates?.[0]]} pathOptions={{ fillColor: "blue", color: "blue" }} 
                        radius={1500}
                        stroke={true}
                        fillOpacity={0.9}
                        weight={5}
                      >
                          <Popup>
                          <ZipCodePopupComponent area={area}/>
                          </Popup>
                      </Circle>
                    ) : (
                      <Polygon
                        key={index}
                        positions={configureZipcodeCoordinates(area?.coordinates?.geometry)}
                        pathOptions={{
                          fillColor: area?.color,
                          fillOpacity: 0.7,
                          weight: 1.5,
                          opacity: 0.1,
                          color: 'black'
                        }}
                      >
                        <Popup>
                          {isLoading ? (
                            <CircularProgress size="2rem" style={{ zIndex: 100000 }} />
                          ) : (
                             <ZipCodePopupComponent area={area}/>
                          )}
                        </Popup>
                      </Polygon>
                    )
                  ) : null}
                </>
              );
            })}
            
          {view === 'County' &&
            areasCounty?.length > 0 &&
            areasCounty?.map((area, index) => {
              return (
                <>
                  {area?.coordinates?.coordinates[0] &&
                    area?.coordinates?.coordinates[0].length > 0 &&
                    (area?.coordinates?.type === 'point' ? (
                      <CircleMarker
                        center={area?.polygonCoord}
                        fillColor={area?.color}
                        radius={4}
                        stroke={false}
                        fillOpacity={1}
                        weight={1}
                      />
                    ) : (
                      <Polygon
                        key={index}
                        positions={area?.polygonCoord}
                        pathOptions={{
                          fillColor: area?.color,
                          fillOpacity: 0.7,
                          opacity: 0.1,
                          weight: 1.5,
                          color: 'black'
                        }}
                      >
                        <Popup>
                          {isLoading ? (
                            <CircularProgress size="2rem" style={{ zIndex: 100000 }} />
                          ) : (
                            <div>
                              <div style={{ marginBottom: '7px', fontWeight: 'bold' }}>
                                County: {area?.county}
                              </div>
                              {pivot
                                ?.filter(piv => piv.value !== 'retail_status')
                                ?.map(piv => (
                                  <div style={{ fontSize: '13px', marginBottom: '3px' }}>{`${
                                    piv?.label
                                  } : ${
                                    area?.[piv?.value] === null
                                      ? 0
                                      : piv?.value === 'retail_status'
                                      ? formatNumberUsingUnit(area?.[piv?.value])
                                      : piv?.unit
                                      ? formatNumberUsingUnit(
                                          area?.[piv?.value]?.toFixed(2),
                                          piv?.unit
                                        )
                                      : formatNumberUsingUnit(area?.[piv?.value]?.toFixed(2))
                                  }`}</div>
                                ))}
                            </div>
                          )}
                        </Popup>
                      </Polygon>
                    ))}
                </>
              )
            })}
        </>
      )}
    </MapContainer>
  )
}

export default MapView
